/* =============================================================================
   BOOTSTRAP — App-Init + Event-Bus-Wiring
   =============================================================================
   - bootstrap(): orchestriert loadState, Theme, Customer-Form, Auth-Restore,
     View-Switch, Auth-Pille, Status-Log.
   - registerEventHandlers(): Audit-Log, Push-Notifications, Auto-Mailversand,
     Auth-State-Wechsel, Realtime-INSERT-Push.

   Importiert nichts aus app.js — alle Abhängigkeiten leben in Modulen oder
   am window (für die wenigen verbleibenden inline-onclick-Pfade).
============================================================================ */

import { showToast } from './utils/helpers.js';
import { maybeShowPushNotification, bindGlobalModalEsc } from './utils/dom.js';
import { audit } from './core/audit.js';

/**
 * Bootstrap-Lock (S31, Block D — Memory `feedback_gg_no_view_hijack`).
 *
 * Wird gesetzt, solange `bootstrap()` aktiv ist und selbst das Routing macht
 * (Zeilen ~104-131: Cancel-Link → restoredUser → admin / customer-Fallback).
 * Während das Flag true ist, lässt der `auth:state-changed`-Listener den
 * View-Wechsel-Anteil aus — Bootstrap entscheidet authoritativ, der
 * Listener feuert dennoch alle anderen Side-Effects (Realtime-Sync,
 * Availability-Refresh, Auth-Pille).
 *
 * Ohne diese Sperre konnte Folgendes passieren (S30-Funktionstest-Befund,
 * Jonas: „beim ersten Anmelden werde ich auf Reservieren zurückgeworfen"):
 *
 *  1. HTML default → view-customer sichtbar (display:block im Markup).
 *  2. Bootstrap startet, registriert den auth:state-changed-Listener.
 *  3. Supabase verarbeitet den Magic-Link-Hash, feuert SIGNED_IN/INITIAL_SESSION.
 *  4. storage.js' onAuthStateChange-Listener wartet kurz auf _cachedUser und
 *     emittiert `auth:state-changed` — view-customer ist immer noch sichtbar.
 *  5. Handler erkennt `protectedViewActive` (alte Logik: customer war drin)
 *     → SKIP. Bootstrap kommt später bei Zeile 119 zum `switchView('admin')`.
 *  6. Wenn vor Zeile 119 noch ein zweiter Auth-Event feuert (TOKEN_REFRESHED
 *     direkt nach Sign-In ist im Supabase-SDK normal) oder bei langsamen
 *     RPC-Polls die Reihenfolge kippt, kann ein nachfolgendes
 *     `switchView('customer')` aus dem else-Pfad einsetzen.
 *
 * Mit Lock:
 *  - Bootstrap-Phase: Listener emittiert Side-Effects (Realtime + Availability),
 *    aber NICHT switchView. Bootstrap entscheidet allein.
 *  - Nach Bootstrap: Listener arbeitet normal — Token-Refresh bleibt skip,
 *    KDS/Bar/Cancel geschützt, sonst switchView('admin').
 */
let _bootstrapPhaseActive = false;
import {
  // S83 Mail-Pfad-Konsolidierung: manuelle Re-Send-Buttons (window.send*Email)
  // laufen jetzt über die Outbox/Edge (resend_status_mail). Die JS-Auto-Mail-
  // Wrapper (autoSend* + Source-Gate + Dedup-Lock) wurden entfernt — Auto-Mails
  // laufen seit S43b komplett über DB-Trigger → mail_outbox → Cron-Edge, der
  // Source-Filter lebt in den Triggern. S94: die Received-Mail-Sender
  // (sendReservationReceivedEmail / sendOrderReceivedEmail) wurden entfernt —
  // die erste Mail geht erst nach interner Bestätigung raus.
  sendConfirmationEmail, sendDeclineEmail,
  sendOrderConfirmedEmail,
  sendOrderReadyEmail, sendOrderCancelledEmail
} from './core/email-templates.js';
import { initOrderNotifications } from './views/admin/orders-notifications.js';
// S234 T2 — GoBD-Bon-Catch-up für webhook-bezahlte Stripe-Orders (Cockpit-only).
import { initStripeBonCatchup } from './views/admin/stripe-bon-catchup.js';
// S222 Cut 1 (L1) — Fahrer-Terminal-Reload: den Operator-Layer VOR dem direkten
// Terminal-Einstieg hydrieren (läuft sonst nur beim Admin-Panel-Mount).
import { ensureOperatorLayer } from './views/admin/operator-ui.js';
import { applyBrandTheme } from './views/admin/settings.js';
import { applyBrand, applyBrandLogo } from './core/branding.js';
import { loadState } from './core/state.js';
import { initCustomerForm, armGuestChannelsFailOpen } from './views/customer-form.js';
// S234 T1 — Stripe-Return-UX (?stripe_return/?stripe_cancel) beim Boot auswerten.
import { handleStripeReturnFromUrl } from './core/stripe-checkout.js';
import { bindAdminShortcut, switchView, checkAdminAccessFromUrl } from './views/view-switch.js';
import { renderAuthPill } from './views/admin-login.js';
import { attachRealtimeSync, detachRealtimeSync, restartRealtimeSync } from './realtime.js';
// S144: frischer Magic-Link-Login → Idle-Stempel frisch setzen (s. admin-login.js).
import { markMainActivity } from './core/session-idle.js';
// Tab-Coordinator (S30, Block A) — Side-Effect-Import: das Modul auto-initialisiert
// beim Load die BroadcastChannel-Master-Election und registriert sich am
// window.App.tabCoordinator. storage.js liest die Rolle bei jedem Subscribe.
// Muss VOR attachRealtimeSync importiert sein, damit der erste subscribe direkt
// die Master/Follower-Entscheidung berücksichtigt.
import './core/tab-coordinator.js';

export async function bootstrap() {
  // S267 K1 — Fail-open-Wächter für die Gast-Kanäle VOR dem Settings-Load:
  // bleibt loadState hängen oder scheitert, zeigt die Gastseite nach ~3 s das
  // Standard-Layout statt ewig „Einen Moment …".
  armGuestChannelsFailOpen();
  await loadState();
  applyBrandTheme();
  // S48 Multi-Tenant-Branding-Fix: applyBrand statt applyBrandLogo allein —
  // setzt Logo + Name + Title in einem Rutsch. Hier mit pre-auth-State (kann
  // Pilot oder leer sein); nach _initFromSession wird erneut aufgerufen mit
  // dem korrekten Tenant-State. Race-Condition-Auflösung.
  applyBrand();
  registerEventHandlers();

  // S29 KRITISCH-Fix (Memory-Lesson gg-db-for-critical-data): Bon- und Z-Bon-
  // Archive aus DB nach localStorage synchronisieren — die Renderer
  // (renderBonsArchive, renderZBonsArchive) lesen weiterhin sync aus
  // localStorage, aber DB ist die Truth-Source. Plus: einmalige Migration
  // bei leerer DB + vorhandenen lokalen Daten → lokal-zu-DB push, damit
  // bestehende Test-Daten nicht verloren gehen.
  hydrateArchivesOnce('bootstrap');
  // Order-Notifications-Bus erst NACH registerEventHandlers binden, weil
  // dort der Auto-Mail-Pfad schon dranhängt — wir wollen die Notifications
  // als parallele Subscriber, nicht als Konkurrenz.
  initOrderNotifications();

  // S82-2: Tracking-Link (#track=) SOFORT sichtbar machen — VOR den await-
  // Schritten (refreshAvailability + _initFromSession), sonst sieht ein Gast,
  // der den Tracking-Link aus der Receipt-Mail klickt, eine weiße Seite bis
  // beide Netz-Roundtrips durch sind (Jonas-Befund S82-2). Reiner Read-only-
  // Pfad: hier nur View-Switch + Skeleton; der echte RPC-Fetch + Polling läuft
  // weiter unten unverändert über checkTrackingRequestFromUrl(). Doppelter
  // switchView('tracking')/renderTrackingLoading ist idempotent → kein
  // Doppel-Fetch (Fetch/Poll startet nur unten).
  if (/^#track=/.test(window.location.hash || '')) {
    if (typeof window.renderTrackingLoading === 'function') window.renderTrackingLoading();
    switchView('tracking');
  }

  // Availability-Cache für das Customer-Form (No-Op bei authenticated Admins).
  try {
    await window.App.storage.reservations.refreshAvailability();
  } catch (e) {
    console.warn('[bootstrap] refreshAvailability fehlgeschlagen — Live-Verfügbarkeit zeigt vorerst „frei":', e);
  }
  initCustomerForm();
  // S234 T1 — Rückkehr von Stripe Checkout (?stripe_return/?stripe_cancel):
  // Erfolgs-/Abbruch-Feedback + ggf. Routing in die Live-Status-Seite. No-Op ohne
  // den Query-Param. Früh, damit der Gast sofort Feedback sieht.
  try { handleStripeReturnFromUrl(); } catch (e) { console.warn('[bootstrap] Stripe-Return-Handling übersprungen:', e); }
  bindAdminShortcut();
  // S224 CUT B — globaler ESC-Close fürs geteilte #modal-overlay (einmalig).
  bindGlobalModalEsc();

  // Auth-Session wiederherstellen
  // Audit 2026-05-20, Finding M-05: Die likely-authed-Anti-Flash-Class wird
  // jetzt in einem try/finally GARANTIERT entfernt — egal welchen Pfad
  // _initFromSession nimmt. Vorher hing sie nur am else-Branch, was bei
  // Exception oder unerwartetem Auth-State zu stuck-likely-authed führen
  // konnte (Customer-View blieb für anonyme Sessions versteckt).
  //
  // S31, Block D: _bootstrapPhaseActive ab hier setzen — der
  // auth:state-changed-Listener emittiert weiterhin Realtime-Sync und
  // Availability-Refresh, lässt aber das View-Routing der Bootstrap-Logik
  // unterhalb. Wird im finally-Block wieder freigegeben.
  _bootstrapPhaseActive = true;
  let restoredUser = null;
  try {
    if (window.App.auth && typeof window.App.auth._initFromSession === 'function') {
      try { restoredUser = await window.App.auth._initFromSession(); }
      catch (e) { console.error('[bootstrap] auth session init failed:', e); }
    }
    // S48 Multi-Tenant-Branding-Fix: nach _initFromSession ist der korrekte
    // Tenant-State geladen (settings.restaurantName, settings.brand.logoUrl
    // gehören jetzt zum eingeloggten User). applyBrand() nochmal feuern,
    // damit Brand-Slots (Header-Name, Logo, Document-Title) den richtigen
    // Tenant zeigen. Löst die Race-Condition aus S47, bei der das initiale
    // applyBrandLogo() vor Auth-Restore lief und nie wieder.
    if (restoredUser) applyBrand();

    // URL-Aufräumen: ?login=success aus dem Magic-Link-Callback entfernen.
    // Achtung: wenn ein #cancel=<token>-Hash anliegt, soll der NICHT mit
    // überschrieben werden (Funktionstest Audit-S21 Bugfix). Daher den
    // Hash unverändert lassen, falls Cancel-Link vorliegt.
    const isCancelLink = /^#cancel=/.test(window.location.hash || '');
    // REF-7c: Live-Tracking-Link (#track=<token>) — gleiche Hash-Priorität
    // wie der Cancel-Link, rein lesend.
    const isTrackingLink = /^#track=/.test(window.location.hash || '');
    // S226 — Newsletter-Abo-Deep-Links (Landing/Confirm/Unsub), public + prefetch-sicher.
    const isNewsletterLink = /^#newsletter(\/|-confirm\/|-unsub\/)/.test(window.location.hash || '');
    // S244 Cut B — Tisch-QR. Gleiche Prioritaet wie die anderen Gast-Deep-Links:
    // wer den Aufsteller scannt, will bestellen — auch wenn zufaellig noch eine
    // Admin-Session im selben Browser liegt.
    const isTableLink = /^#tisch\//.test(window.location.hash || '');
    // S262 Cut A — Dienstplan-Token-Link aus der Abfrage-Mail. Gleiche Prioritaet:
    // der Mitarbeiter will seine Woche eintragen, auch wenn im selben Browser
    // noch eine Cockpit-Session liegt.
    const isScheduleLink = /^#dienstplan\//.test(window.location.hash || '');
    const cameFromMagicLink = window.location.search.includes('login=success');
    // S144 Rootcause-Fix: Ein frisch geklickter Magic-Link ist echte
    // interaktive Aktivität. Idle-Stempel JETZT frisch setzen, BEVOR weiter
    // unten switchView('admin') → showAdminPanel() → initAdminShell() den
    // Idle-Boot-Check macht. Ein stale `gg_main_last_activity` aus einer nicht
    // sauber beendeten Vorsession würde sonst sofort wieder auslogen.
    // Bewusst NUR bei cameFromMagicLink, NICHT bei reinem INITIAL_SESSION-
    // Restore (persistierte Session ohne frischen Login) — sonst bräche das
    // „Browser war stundenlang zu"-Auto-Logout-Feature (S140).
    if (cameFromMagicLink && restoredUser) {
      markMainActivity();
    }
    if (cameFromMagicLink) {
      const targetHash = (isCancelLink || isTrackingLink || isNewsletterLink || isTableLink || isScheduleLink)
        ? window.location.hash
        : (restoredUser ? '#admin' : '');
      const cleanUrl = window.location.pathname + targetHash;
      history.replaceState(null, '', cleanUrl);
    }

    // Funktionstest Audit-S21: Cancel-Link (#cancel=<token>) hat ABSOLUTE
    // Priorität — auch bei eingeloggten Admins. Vorher überschrieb der
    // restoredUser-Branch den Hash mit '#admin', sodass Admins, die einen
    // Storno-Link aus einer Mail klickten, im Cockpit landeten statt in
    // der Storno-Detail-Card. checkCancelRequestFromUrl prüft den Hash
    // und wechselt die View; bei Treffer ist die View bereits 'cancel'.
    let handledByCancel = false;
    if (isCancelLink && typeof window.checkCancelRequestFromUrl === 'function') {
      handledByCancel = window.checkCancelRequestFromUrl();
      // Bei eingeloggten Admins trotzdem Realtime-Sync anhängen — falls
      // sie nach erfolgreicher Stornierung manuell ins Cockpit wechseln,
      // sollen Live-Updates schon laufen.
      if (handledByCancel && restoredUser) {
        attachRealtimeSync();
      }
    }

    // REF-7c: Tracking-Link dispatchen (nur wenn kein Cancel-Link griff).
    let handledByTracking = false;
    if (!handledByCancel && isTrackingLink
        && typeof window.checkTrackingRequestFromUrl === 'function') {
      handledByTracking = window.checkTrackingRequestFromUrl();
      if (handledByTracking && restoredUser) {
        attachRealtimeSync();
      }
    }

    // S226: Newsletter-Abo-Link dispatchen (nur wenn Cancel/Tracking nicht griffen).
    let handledByNewsletter = false;
    if (!handledByCancel && !handledByTracking && isNewsletterLink
        && typeof window.checkNewsletterRequestFromUrl === 'function') {
      handledByNewsletter = window.checkNewsletterRequestFromUrl();
      if (handledByNewsletter && restoredUser) {
        attachRealtimeSync();
      }
    }

    // S244: Tisch-Link dispatchen (nur wenn keiner der anderen griff).
    let handledByTable = false;
    if (!handledByCancel && !handledByTracking && !handledByNewsletter && isTableLink
        && typeof window.checkTableRequestFromUrl === 'function') {
      handledByTable = window.checkTableRequestFromUrl();
      if (handledByTable && restoredUser) {
        attachRealtimeSync();
      }
    }

    // S262: Dienstplan-Link dispatchen (nur wenn keiner der anderen griff).
    let handledBySchedule = false;
    if (!handledByCancel && !handledByTracking && !handledByNewsletter && !handledByTable && isScheduleLink
        && typeof window.checkScheduleRequestFromUrl === 'function') {
      handledBySchedule = window.checkScheduleRequestFromUrl();
      if (handledBySchedule && restoredUser) {
        attachRealtimeSync();
      }
    }

    if (!handledByCancel && !handledByTracking && !handledByNewsletter && !handledByTable && !handledBySchedule) {
      if (restoredUser) {
        // S222 Cut 1 (L1) — Fahrer-Terminal-Deep-Link/Reload: enterDriverView()
        // setzt den Hash auf `#driver`. Bisher überschrieb der Boot ihn mit
        // `#admin` + switchView('admin') → ein reiner Fahrer fiel bei jedem
        // Reload ins Cockpit (das S209-Gate greift nicht, weil hydrateOperator
        // erst beim Admin-Panel-Mount läuft → Operator-State ist zum Gate-
        // Zeitpunkt null).
        //
        // Jonas 2026-07-05: direkt ins Terminal, KEIN Admin-Zwischenframe.
        // Voraussetzung: der Operator-Layer wird ZUERST hydratisiert
        // (ensureOperatorLayer läuft sonst nur beim Admin-Panel-Mount) — sonst
        // ruft das Terminal list_driver_orders mit session_id=null →
        // assert_operator_role wirft OPERATOR_FORBIDDEN. NUR wenn danach wirklich
        // ein Fahrer als Operator aktiv ist → Terminal; sonst (Session server-
        // seitig beendet / kein Fahrer mehr) sauberer Cockpit-Fallback.
        const _hash = window.location.hash || '';
        let _wentToDriver = false;
        if (_hash.startsWith('#driver')) {
          try { await ensureOperatorLayer(); }
          catch (e) { console.warn('[bootstrap] ensureOperatorLayer (driver-boot):', e && e.message); }
          const _op = (window.App && window.App.operator && typeof window.App.operator.getOperator === 'function')
            ? window.App.operator.getOperator() : null;
          if (_op && _op.role === 'driver') {
            switchView('driver');
            _wentToDriver = true;
          }
        }
        if (!_wentToDriver) {
          if (!_hash.startsWith('#admin')) {
            history.replaceState(null, '', window.location.pathname + '#admin');
          }
          switchView('admin');
        }
        attachRealtimeSync();
        if (cameFromMagicLink) {
          showToast('Erfolgreich angemeldet als ' + restoredUser.email, 'success');
        }
        // S136 Cut B: kein automatischer Vortags-Z-Bon mehr (Z-Bon nur manuell
        // über den Tagesabschluss-Tab). Der frühere S93-B-Auto-Trigger ist raus.
      } else {
        const wantsAdmin = window.location.hash === '#admin';
        if (wantsAdmin) {
          checkAdminAccessFromUrl();
        } else {
          switchView('customer');
        }
      }
    }
  } finally {
    // Anti-Flash-Class IMMER entfernen — auch wenn ein Error vorher die
    // restoredUser-Setzung verhindert hat. Der initiale CSS-Hide-Effekt der
    // Customer-View darf maximal so lange wirken wie _initFromSession läuft.
    document.documentElement.classList.remove('likely-authed');
    // S31, Block D: Bootstrap-Phase Ende — ab jetzt darf der
    // auth:state-changed-Listener wieder selbst View-Wechsel anstoßen
    // (z.B. bei späterem Sign-In über Modal-Login oder Magic-Link in einem
    // zweiten Tab).
    _bootstrapPhaseActive = false;
  }
  await renderAuthPill();

  console.info(`[Grill & Green] ${window.AppConfig.appName} v${window.AppConfig.appVersion} ready · ` +
               `storage=${window.AppConfig.backend.storage} mailer=${window.App.mailer.getProviderName()} auth=${window.App.auth.getProviderName()}` +
               (restoredUser ? ` · eingeloggt als ${restoredUser.email} (${restoredUser.role})` : ' · nicht eingeloggt'));
}

/**
 * Bon-/Z-Bon-Archive aus Supabase in den In-Memory-State laden.
 *
 * Phase 31, Session 29 — Memory-Lesson `gg-db-for-critical-data`:
 *  - `public.bon_archive` und `public.zbon_archive` sind die einzige
 *    Persistenz. localStorage wird NICHT mehr beschrieben.
 *  - `App.storage.bonArchive.hydrate()` lädt die DB-Daten in
 *    `_state.bonArchive` (Object {orderId: entry}), aus dem die sync-
 *    Renderer (renderBonsArchive, openOrderDetail) lesen.
 *  - One-Time-Migration: wenn DB leer aber alter `gg_bon_archive_v1`/
 *    `gg_zbon_archive_v1`-localStorage-Stand vorhanden ist, werden die
 *    lokalen Bestände einmalig in die DB gepusht und der localStorage-
 *    Key danach gelöscht. So gehen Test-Daten nicht verloren.
 */
/**
 * S238-HF5 — Darf ein `auth:state-changed`-Event einen VOLLEN Archiv-Hydrate
 * auslösen? Pure (Vitest).
 *
 * Supabase liefert `ev === 'SIGNED_IN'` auch bei jedem TOKEN_REFRESHED (~alle
 * 50 Min, in JEDEM offenen Tab). Beide Archiv-Hydrates laden per `select=*`
 * inklusive der HTML-Snapshots (`bon_archive` ~3 MB). Am 25.08. summierte sich
 * das mit Vite-Full-Reloads auf ~250 Voll-Hydrates in 15 Minuten →
 * Statement-Timeouts → Postgres-Neustart auf der Micro-Instanz.
 *
 * Erlaubt bleibt der Hydrate genau dann, wenn sich die DATENGRUNDLAGE ändert:
 * echter Login (`rawEvent === 'SIGNED_IN'`) oder Tenant-Wechsel. Bei einem
 * Token-Refresh zieht der Realtime-Sync ohnehin alles Neue nach.
 */
export function shouldHydrateArchivesOnAuth({ rawEvent, tenantSwitched } = {}) {
  return rawEvent === 'SIGNED_IN' || tenantSwitched === true;
}

/* -----------------------------------------------------------------------------
   S240-HF7.2 — Archiv-Hydrate genau EINMAL pro Boot/Tenant
   -----------------------------------------------------------------------------
   BEFUND (gemessen, Dev 2026-08-26): der Boot rief `hydrateArchivesFromDb()`
   ZWEIMAL — einmal im Boot-Pfad, einmal im `auth:state-changed`-Handler
   (SIGNED_IN). Beide Läufe zogen parallel je ~23 MB Bon-Archiv (100 s + 84 s),
   liefen ins statement timeout und lösten einen Postgres-Neustart aus.
   Der zweite Lauf bringt nie neue Daten: der erste hat dieselbe Tabelle im selben
   Tenant gerade geladen, alles Weitere kommt über Realtime.

   Dedupe über das laufende Promise (nicht über ein Flag NACH dem Lauf) — sonst
   startet der zweite Aufruf, solange der erste noch fliegt. Der Key ist der
   Tenant: ein Tenant-Wechsel MUSS neu hydratisieren.
----------------------------------------------------------------------------- */
/**
 * S240-HF7.3 — Gegenprobe für ein LEER gemeldetes Archiv.
 *
 * Ein `select` mit 0 Zeilen und ohne `error` kann zweierlei heißen: die Tabelle
 * ist wirklich leer — oder der Request ist ins statement timeout gelaufen und
 * hat einen leeren Body geliefert (genau so passiert am 26.08.). Ein
 * `head+count`-Request ist winzig und beantwortet das eindeutig.
 *
 * @returns {Promise<boolean>} true = wirklich leer (Hydrate war erfolgreich)
 */
async function _verifyEmptyArchive(table) {
  const App = window.App;
  const supa = App && App.supabase;
  const rid = (App && App.auth && typeof App.auth.getCurrentRestaurantIdSync === 'function')
    ? App.auth.getCurrentRestaurantIdSync() : null;
  if (!supa || !rid) return false;
  try {
    const { count, error } = await supa.from(table)
      .select('id', { count: 'exact', head: true })
      .eq('restaurant_id', rid);
    if (error) {
      console.warn('[hydrate] count-Gegenprobe ' + table + ' fehlgeschlagen:', error.message || error);
      return false;
    }
    if (Number(count) > 0) {
      console.warn('[hydrate] ' + table + ' meldete 0 Zeilen, die DB hat aber ' + count
        + ' — Hydrate gilt als FEHLGESCHLAGEN (Retry beim nächsten Anlass).');
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[hydrate] count-Gegenprobe ' + table + ' warf:', e && (e.message || e));
    return false;
  }
}

let _archiveHydratePromise = null;
let _archiveHydrateTenant = null;

function hydrateArchivesOnce(reason) {
  const App = window.App;
  // Tenant-Key: `auth.getCurrentRestaurantIdSync()` ist die öffentliche Quelle
  // (storage.js liest intern dieselbe); `null` vor dem Login ist ok — der
  // SIGNED_IN-Lauf hat dann einen anderen Key und hydratisiert.
  const rid = (App && App.auth && typeof App.auth.getCurrentRestaurantIdSync === 'function')
    ? App.auth.getCurrentRestaurantIdSync()
    : null;
  if (_archiveHydratePromise && _archiveHydrateTenant === rid) {
    console.info('[hydrate] Archive bereits geladen/ladend (' + reason + ') — übersprungen.');
    return _archiveHydratePromise;
  }
  _archiveHydrateTenant = rid;
  _archiveHydratePromise = hydrateArchivesFromDb()
    .catch(e => {
      console.warn('[hydrate] archive hydrate failed (' + reason + '):', e);
      // Fehlschlag darf nicht „erledigt" bedeuten — nächster Anlass darf es
      // erneut versuchen (der Catch-up-Boot-Scan wartet auf das Erfolgs-Flag).
      _archiveHydratePromise = null;
      _archiveHydrateTenant = null;
    });
  return _archiveHydratePromise;
}

async function hydrateArchivesFromDb() {
  const App = window.App;
  if (!App || !App.storage || !App.supabase) return;
  if (!(App.auth && App.auth.getCurrentUserSync && App.auth.getCurrentUserSync())) return;

  // ----- Bon-Archiv -----
  try {
    // One-Time-Migration aus altem localStorage-Stand
    const legacyBonsRaw = localStorage.getItem('gg_bon_archive_v1');
    if (legacyBonsRaw) {
      const dbBons = await App.storage.bonArchive.listAll();
      if (Object.keys(dbBons).length === 0) {
        const legacy = (() => { try { return JSON.parse(legacyBonsRaw) || {}; } catch (_) { return {}; } })();
        const ids = Object.keys(legacy);
        if (ids.length > 0) {
          console.info('[hydrate] One-Time-Migration: pushe ' + ids.length + ' lokale Bons in bon_archive…');
          for (const id of ids) {
            try { await App.storage.bonArchive.upsert(legacy[id]); }
            catch (e) { console.error('[hydrate] bon upsert ' + id + ' failed:', e); }
          }
        }
      }
      // Legacy-Key löschen — DB ist jetzt Truth-Source, localStorage soll nicht mehr verwirren
      localStorage.removeItem('gg_bon_archive_v1');
    }
    const bonCache = await App.storage.bonArchive.hydrate();
    // S238-HF4 — Erfolgs-Flag für den Stripe-Bon-Catch-up: der Boot-Scan darf
    // NICHT auf einem leeren Cache laufen, der nur wegen eines fehlgeschlagenen
    // Hydrate leer ist (DB-Ausfall 25.08. → zweite Beleg-Mail an einen Gast).
    App.storage._archiveHydrateOk = App.storage._archiveHydrateOk || {};
    // S240-HF7.3 — „0 Zeilen ohne Fehler" ist NICHT automatisch „leere Tabelle".
    // Am 26.08. liefen zwei 23-MB-Requests ins statement timeout und kamen mit
    // 0 Zeilen und OHNE error zurück → das Flag stand auf true, das Archiv war
    // leer, und der Stripe-Catch-up hätte auf diesem Stand gearbeitet.
    // Jetzt: 0 Zeilen werden per count-Gegenprobe verifiziert.
    const bonCount = Object.keys(bonCache).length;
    App.storage._archiveHydrateOk.bonArchive =
      bonCount > 0 ? true : await _verifyEmptyArchive('bon_archive');
    console.info('[hydrate] bon_archive: ' + bonCount + ' Eintrag/Einträge geladen'
      + (bonCount === 0 ? ' (leer verifiziert: ' + App.storage._archiveHydrateOk.bonArchive + ')' : '') + '.');
  } catch (e) {
    if (App.storage._archiveHydrateOk) App.storage._archiveHydrateOk.bonArchive = false;
    console.warn('[hydrate] bon_archive hydrate failed:', e);
  }

  // ----- Z-Bon-Archiv -----
  try {
    const legacyZRaw = localStorage.getItem('gg_zbon_archive_v1');
    if (legacyZRaw) {
      const dbZ = await App.storage.zbonArchive.listAll();
      if (Object.keys(dbZ).length === 0) {
        const legacy = (() => { try { return JSON.parse(legacyZRaw) || {}; } catch (_) { return {}; } })();
        const keys = Object.keys(legacy);
        if (keys.length > 0) {
          console.info('[hydrate] One-Time-Migration: pushe ' + keys.length + ' lokale Z-Bons in zbon_archive…');
          for (const key of keys) {
            const entry = legacy[key];
            if (!entry.date) entry.date = /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : null;
            if (!entry.date) { console.warn('[hydrate] Z-Bon ohne Datum übersprungen:', key); continue; }
            try { await App.storage.zbonArchive.upsert(entry); }
            catch (e) { console.error('[hydrate] zbon upsert ' + key + ' failed:', e); }
          }
        }
      }
      localStorage.removeItem('gg_zbon_archive_v1');
    }
    const zbonCache = await App.storage.zbonArchive.hydrate();
    console.info('[hydrate] zbon_archive: ' + Object.keys(zbonCache).length + ' Eintrag/Einträge geladen.');
  } catch (e) {
    console.warn('[hydrate] zbon_archive hydrate failed:', e);
  }
}

export function registerEventHandlers() {
  const App = window.App;

  // Reservation-Events → Audit-Log
  App.events.on('reservation:created',   p => audit('reservation.created',   { id: p.id, name: p.name, guests: p.guests }));
  App.events.on('reservation:confirmed', p => audit('reservation.confirmed', { id: p.id, name: p.name }));
  App.events.on('reservation:declined',  p => audit('reservation.declined',  { id: p.id, name: p.name }));
  App.events.on('reservation:cancelled', p => audit('reservation.cancelled', { id: p.reservation.id, by: p.by }));
  App.events.on('reservation:noshow',    p => audit('reservation.noshow',    { id: p.id, name: p.name }));
  App.events.on('walkin:created',        p => audit('walkin.created',        { id: p.id, name: p.name, guests: p.guests }));
  App.events.on('waitlist:joined',       p => audit('waitlist.joined',       { id: p.id, name: p.name }));

  // Order-Events → Audit-Log (Buchhaltungs-/Status-Spur)
  App.events.on('order:created',         p => audit('order.created',         { id: p && p.id, name: p && (p.customerName || p.name), orderNumber: p && (p.orderNumber || p.order_number) }));
  App.events.on('order:status_changed',  p => audit('order.status_changed',  { id: p && p.order && p.order.id, from: p && p.from, to: p && p.to }));
  App.events.on('order:cancelled',       p => audit('order.cancelled',       { id: p && p.order && p.order.id, by: p && p.by }));
  App.events.on('order:paid',            p => audit('order.paid',            { id: p && p.order && p.order.id, method: p && p.method, invoiceNumber: p && p.order && (p.order.invoiceNumber || p.order.invoice_number) }));
  App.events.on('order:mail-sent',       p => audit('order.mail-sent',       { id: p && p.orderId, kind: p && p.kind, to: p && p.to }));

  // S234 T2 — GoBD-Bon-Catch-up für webhook-bezahlte Stripe-Orders (Boot-Scan +
  // order:realtime/order:paid-Trigger). No-Op im Gast-Kontext (kein Bon-Archiv).
  try { initStripeBonCatchup(); } catch (e) { console.warn('[bootstrap] Stripe-Bon-Catchup-Init übersprungen:', e); }

  // Browser-Notification bei eigenen Inserts
  if (window.AppConfig.features.pushNotifications && 'Notification' in window) {
    App.events.on('reservation:created', r => maybeShowPushNotification(r));
    App.events.on('walkin:created',      r => maybeShowPushNotification(r));
  }

  // Auto-Mailversand
  //
  // Stand Session 43b (M-08 Vollaktivierung): die Status-Mails
  // (confirmed/ready/declined/cancelled für Reservierungen + Orders) laufen
  // jetzt server-seitig über die `mail_outbox`-Queue + Cron-Worker
  // `process-mail-outbox`. Die DB-Trigger (Phase 34 + bestehender
  // enqueue_order_cancel_mail aus S33) enqueue'n bei jedem Status-Change
  // automatisch. JS-Auto-Mail-Listener für diese Pfade sind hier entfernt,
  // sonst gäbe es Doppel-Mails (DB-Trigger + JS-Sync zur gleichen Zeit).
  //
  // **S94 — Received-Mails komplett entfernt:** Die Eingangsbestätigungen
  // (Reservation + Order) wurden schon in S74 nicht mehr automatisch versendet
  // (Confirmation-Screen ersetzt sie); in S94 sind auch die JS-Sender selbst
  // weg. Die erste echte Mail geht grundsätzlich erst nach interner Bestätigung
  // raus (DB-Trigger enqueue_*_confirmed_mail). Kein synchroner JS-Mail-Pfad
  // mehr beim Submit.
  //
  // Das Settings-Flag `settings.useOutboxForStatusMails` ist seit
  // Migration 34c per Default `true` für alle Tenants — die Trigger sind
  // damit auch aktiv. Tenants können das Flag manuell deaktivieren
  // (Settings → Vorlagen → Mail-Outbox-Schalter), wenn sie wieder den
  // alten JS-Sync-Pfad wollen — dann müssen sie aber auch hier wieder die
  // Listener reaktivieren.
  //
  // Memory-Lessons: feedback_gg_hard_way (voller Architektur-Cut statt
  // halber Übergang), feedback_gg_db_for_critical_data (Status-Mails sind
  // GoBD-relevant — Outbox-Queue mit Retry-Logic ist die robustere Schicht).
  const mailProvider = App.mailer && App.mailer.getProviderName && App.mailer.getProviderName();
  if (mailProvider && mailProvider !== 'mailto') {
    // ENTFERNT in S74 (Konzept-Cut) + S94 (Sender gelöscht): die received-
    // Eingangsbestätigungen (reservation:created / order:created) wurden nie
    // wieder automatisch versendet — der Confirmation-Screen (#customer-success)
    // ist die visuelle Eingangsbestätigung, die erste echte Mail folgt erst beim
    // Operator-Confirm via DB-Trigger enqueue_*_confirmed_mail (Phase 73c).
    // S94 hat zusätzlich die JS-Sender (sendReservationReceivedEmail /
    // sendOrderReceivedEmail) aus email-templates.js entfernt.
    //
    // Memory-Lessons: feedback_gg_hard_way (voller Konzept-Cut),
    // project_gg_firma_rollen (Customer-Experience-Optimierung).

    // ENTFERNT in S43b (M-08 Vollaktivierung) — laufen jetzt über mail_outbox:
    //   - reservation:confirmed  → trg_reservations_enqueue_confirmed_mail
    //   - reservation:declined   → trg_reservations_enqueue_declined_mail
    //   - order:status_changed   → trg_orders_enqueue_confirmed_mail (für 'confirmed')
    //                              trg_orders_enqueue_ready_mail     (für 'ready')
    //   - order:cancelled        → trg_orders_enqueue_cancel_mail (existiert seit S33)
    //
    // Hatten alle das Doppel-Mail-Risiko: DB-Trigger setzt einen Outbox-Eintrag,
    // gleichzeitig sendet der JS-Pfad synchron via Resend → Gast bekommt 2 Mails.
    // Im Fall von order:cancelled bestand das Problem seit S33 latent (selten
    // bemerkt, weil im Testbetrieb selten storniert wurde).

    console.info('[Grill & Green] Auto-Mailversand aktiv via ' + mailProvider + ' (Status-Mails über mail_outbox; keine Received-Mails)');
  }

  // Auth-State-Wechsel
  App.events.on('auth:state-changed', async ({ event: ev, rawEvent, tenantSwitched, restaurantId, previousRestaurantId }) => {
    if (ev === 'SIGNED_IN') {
      const isFreshLogin = rawEvent === 'SIGNED_IN';
      const isTokenRefresh = rawEvent === 'TOKEN_REFRESHED';

      // S29 KRITISCH-Fix: nach Login frisch aus DB hydraten — der Bon-/
      // Z-Bon-Archive-Cache könnte vom letzten User / Restaurant stammen oder
      // durch Browser-Cleanup geleert worden sein.
      //
      // S238-HF5: NUR bei echtem Login oder Tenant-Wechsel — Begründung +
      // Entscheidungslogik in `shouldHydrateArchivesOnAuth` (pure, getestet).
      if (shouldHydrateArchivesOnAuth({ rawEvent, tenantSwitched })) {
        // S240-HF7.2: `hydrateArchivesOnce` dedupliziert gegen den Boot-Pfad —
        // beim Tenant-Wechsel ist der Key ein anderer, dann wird neu geladen.
        hydrateArchivesOnce(tenantSwitched ? 'tenant-switch' : 'signed-in');
      }
      // Phase 20 (Session 22) + S31 Block D (Memory `feedback_gg_no_view_hijack`):
      //
      // Operative Arbeitssichten (KDS/Bar/Delivery/Fahrer-Terminal/Cancel)
      // dürfen NIE durch einen selbstständigen View-Switch verloren gehen —
      // Küche/Theke/Liefer-Cockpit arbeiten in Vollbild-Modi, das Fahrer-
      // Terminal ist die mobile Vollbild-Sicht (S222 L2), der Storno-Pfad zeigt
      // eine Detail-Card, die unterbrechungsfrei laufen muss.
      //
      // **Customer-View ist NICHT mehr geschützt** (S31-Bugfix): sie ist
      // der HTML-Default und gleichzeitig der häufigste Landing-Punkt nach
      // einem Magic-Link-Callback. Ein eingeloggter User, der bei aktivem
      // Auth-Event auf view-customer steht, will fast immer ins Cockpit.
      // Edge-Case (Admin schaut sich Customer-Sicht an, dann Token-Refresh):
      // wird über den `isTokenRefresh`-Skip abgefangen — bei TOKEN_REFRESHED
      // bleibt JEDE View erhalten.
      //
      // S31 Block D: Während `_bootstrapPhaseActive` lässt der Listener
      // den View-Wechsel komplett aus — Bootstrap routet selbst (siehe
      // Kommentar oben am Modul). Verhindert Race-Conditions zwischen
      // INITIAL_SESSION/SIGNED_IN-Replays und dem bootstrap-eigenen
      // switchView('admin').
      const isViewVisible = (id) => document.getElementById(id)?.style.display === 'block';
      // S222 Cut 1 (L2) — view-driver + view-delivery ergänzt: ein SIGNED_IN-Replay
      // (Supabase feuert das u.a. bei Tab-/App-Refokus nach Token-Recovery — auf
      // dem Fahrer-Handy nach Standby der Normalfall) würde sonst switchView('admin')
      // auslösen und den Fahrer aus dem Terminal werfen. Delivery-Cockpit ist die
      // gleiche Klasse operativer Vollbild-Sicht wie KDS/Bar.
      const protectedViewActive =
            isViewVisible('view-cancel')     ||
            isViewVisible('view-tracking')   ||
            isViewVisible('view-newsletter') ||   // S226-HF2 (#2): sonst wirft SIGNED_IN-Replay den Abo-Gast ins Cockpit
            isViewVisible('view-schedule')   ||   // S262: der Mitarbeiter tippt gerade seine Woche ein
            isViewVisible('view-kds')        ||
            isViewVisible('view-bar')        ||
            isViewVisible('view-delivery')   ||
            isViewVisible('view-driver');

      if (_bootstrapPhaseActive) {
        console.info('[auth:state-changed] ' + (rawEvent || 'SIGNED_IN') + ' während Bootstrap — View-Routing übernimmt bootstrap() selbst.');
      } else if (isTokenRefresh) {
        console.info('[auth:state-changed] TOKEN_REFRESHED ohne View-Wechsel.');
      } else if (protectedViewActive) {
        console.info('[auth:state-changed] ' + (rawEvent || 'SIGNED_IN') + ' ohne View-Wechsel — operative Sicht aktiv (cancel/kds/bar/delivery/driver), User-Aktion wird nicht unterbrochen.');
      } else if (isFreshLogin) {
        // Frischer manueller Login: User landet bewusst im Admin-Cockpit
        if (!window.location.hash.startsWith('#admin')) {
          history.replaceState(null, '', window.location.pathname + '#admin');
        }
        switchView('admin');
      } else {
        // INITIAL_SESSION ohne aktive geschützte View → admin anzeigen
        switchView('admin');
      }
      // Tenant-Wechsel-Pfad (F-04): den Realtime-Channel mit dem alten Filter
      // schließen und mit dem neuen Tenant-Filter neu öffnen. Wirkt sich erst
      // mit Multi-Tenant-Owner-Switch praktisch aus; im Single-Tenant-Setup
      // ist tenantSwitched immer false und wir nehmen den normalen attach-Pfad.
      if (tenantSwitched) {
        console.info('[auth:state-changed] Tenant-Wechsel ' + previousRestaurantId + ' → ' + restaurantId + ' — Realtime wird neu verbunden.');
        restartRealtimeSync();
      } else {
        attachRealtimeSync();
      }
      // S48 Multi-Tenant-Branding-Fix: Brand-Slots (Header-Name, Logo,
      // Document-Title) nach Auth/Tenant-Wechsel zentral neu rendern.
      // Wirkt für: frischer Magic-Link-Login (Cold-Boot), Tab-übergreifender
      // Sign-In (zweiter Tab), Tenant-Switch durch Multi-Tenant-Owner.
      // applyBrand() ist idempotent + defensiv — kein Setzen wenn keine
      // DOM-Slots vorhanden. Memory `project_gg_firma_rollen`.
      try { applyBrand(); }
      catch (e) { console.warn('[auth:state-changed] applyBrand:', e); }
      // S31 Block D: Toast nur, wenn Bootstrap nicht aktiv ist — bootstrap
      // selbst sendet bereits den „Erfolgreich angemeldet als …"-Toast mit
      // E-Mail (Zeile 121-123). Doppelter Toast wäre sonst spürbar.
      if (isFreshLogin && !_bootstrapPhaseActive) {
        showToast('Erfolgreich angemeldet', 'success');
      }
      // S136 Cut B: Vortags-Z-Bon-Auto-Archivierung entfernt — Z-Bon nur noch
      // manuell über den Tagesabschluss-Tab (kein automatischer Tagesabschluss).
    } else if (ev === 'SIGNED_OUT') {
      const loginEl = document.getElementById('admin-login');
      const panelEl = document.getElementById('admin-panel');
      if (loginEl) loginEl.style.display = 'block';
      if (panelEl) panelEl.style.display = 'none';
      detachRealtimeSync();
      try {
        await window.App.storage.reservations.refreshAvailability({ force: true });
      } catch (e) {
        console.warn('[auth:state-changed] refreshAvailability:', e);
      }
      // S48 Multi-Tenant-Branding-Fix: nach Logout zeigt der State wieder
      // den Pilot-Default (oder leer). Brand-Slots zurücksetzen, damit nicht
      // der Tenant-Name des gerade ausgeloggten Users im Header stehen bleibt.
      try { applyBrand(); }
      catch (e) { console.warn('[auth:state-changed/signed-out] applyBrand:', e); }
    }
    await renderAuthPill();
  });

  // Realtime-INSERT → optional Push-Notification
  if (window.AppConfig.features.pushNotifications && 'Notification' in window) {
    App.events.on('reservation:realtime', payload => {
      if (!payload || payload.eventType !== 'INSERT' || !payload.new) return;
      const r = payload.new;
      maybeShowPushNotification({
        id: r.id, name: r.name, guests: r.guests, date: r.date, time: r.time
      });
    });
  }
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, { bootstrap, registerEventHandlers });
}
